import React, { useEffect, useState } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { useContentSetting } from "../../../Hooks/ContentSetting";
import {
  textPrimary, textSecondary, textMuted, font, accent, surface, border, shadow, primaryButtonSx,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { get_school_tutors_config_api } from "../../../DAL/SchoolPortal/Tutors";
import TutorsSetupModal from "./TutorsSetupModal";

export const CAN_MANAGE_ROLES = ["primary_admin", "staff_admin"];

/**
 * The setup gate and page chrome, shared by My Teaching and Bookings & Revenue.
 *
 * WHY THE TWO PAGES ARE SEPARATE NAV ITEMS
 * One "Tutors" item used to hold a CFI's work surface and an owner's P&L. The
 * tab switch inside it solved the dual-ROLE case — a person who both manages
 * and teaches — but not the dual-AUDIENCE one: a CFI opening "Tutors" landed
 * on a page whose other half was the school's revenue, and an owner looking for
 * the month's numbers had to know they were behind a tab called "Tutors".
 * Splitting them means each nav item has one reader.
 *
 * Everything is still gated behind tutorsConfig.setupComplete — an Owner or
 * Manager has to save the school-wide settings once before there is anything
 * for anyone else to do here.
 */
export default function TutorsShell({ title, subtitle, children }) {
  const { userInfo } = useContentSetting();
  const canManage = CAN_MANAGE_ROLES.includes(userInfo?.role);

  const [loading, setLoading] = useState(true);
  const [setupComplete, setSetupComplete] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);

  useEffect(() => {
    get_school_tutors_config_api().then((res) => {
      if (res.code === 200) setSetupComplete(Boolean(res.data?.setupComplete));
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: accent }} /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
      <Typography sx={{ fontSize: "20px", fontWeight: 800, color: textPrimary, fontFamily: font, mb: 0.25 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: "12.5px", color: textSecondary, fontFamily: font, mb: 2 }}>
        {subtitle}
      </Typography>

      {!setupComplete ? (
        <Box sx={{ backgroundColor: surface, border: `1px solid ${border}`, borderRadius: "10px", boxShadow: shadow, p: 4, textAlign: "center" }}>
          {canManage ? (
            <>
              <Typography sx={{ fontFamily: font, fontSize: "15px", fontWeight: 700, color: textPrimary, mb: 1 }}>
                Tutors isn't set up yet
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary, mb: 2.5, maxWidth: 420, mx: "auto" }}>
                Choose your marketplace, payout, and rate settings to open this up for your instructors and students.
              </Typography>
              <Button onClick={() => setShowSetupModal(true)} sx={primaryButtonSx}>Set up Tutors</Button>
            </>
          ) : (
            <Typography sx={{ fontFamily: font, fontSize: "14px", color: textMuted }}>
              Waiting for your manager to set up Tutors.
            </Typography>
          )}
        </Box>
      ) : children}

      {canManage && (
        <TutorsSetupModal
          open={showSetupModal}
          onClose={() => setShowSetupModal(false)}
          onComplete={() => { setShowSetupModal(false); setSetupComplete(true); }}
        />
      )}
    </Box>
  );
}
